/**
 * LanguageSwitcher — top-bar segmented control for the supported locales.
 * Current locale reads from `i18n.language`; selecting an option calls
 * `i18n.changeLanguage` directly — i18next IS the locale store, no app-level
 * state duplicates it.
 */
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { SUPPORTED_LOCALES } from '@/i18n/constants';

export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const { t } = useCopy();
  const current = i18n.language?.split('-')[0] ?? 'en';

  return (
    <ToggleButtonGroup
      value={current}
      exclusive
      size="small"
      onChange={(_event, next: string | null) => {
        if (next) void i18n.changeLanguage(next);
      }}
      aria-label={t('common.language', 'Language')}
      sx={{ mr: 2 }}
    >
      {SUPPORTED_LOCALES.map((code) => {
        const label = t(`lang.${code}`, code.toUpperCase());
        return (
          // color: 'text.primary' (not the default unselected style) so the
          // unselected state meets WCAG AA contrast against the AppBar's
          // `color="default"` background.
          <ToggleButton key={code} value={code} aria-label={label} sx={{ color: 'text.primary' }}>
            {label}
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
}
